<template>
  <div class="f-h gw-templates">
    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <ComponentsList :value="gwtOptions" @edit="paramsEdit" />
    </FiltersZone>
    <div class="f-h-below-filters gw-templates__content">
      <div>
        <div v-for="team in compTeams" :key="team.id" class="gw-templates__team">
          <h2 class="basic-sub-title">
            {{ team.name }}
          </h2>
          <div v-if="team.gwTemplates.length === 0" class="h-v-centerer">
            {{ $t("message.GW_TEAM_NO_TEMPLATES") }}
          </div>
          <div v-else class="gw-templates__col-headers">
            <div class="small-text gw-templates__basis-name">
              {{ $t('message.GW_NAME_PLACEHOLDER') }}
            </div>
            <div class="small-text gw-templates__basis-duration">
              {{ $t('message.DURATION') }}
            </div>
            <div class="small-text gw-templates__basis-button">
              {{ $t('message.START') }}
            </div>
            <div class="small-text gw-templates__basis-button">
              {{ $t('message.EDIT') }}
            </div>
            <div class="small-text gw-templates__basis-button">
              {{ $t('message.DUPLICATE') }}
            </div>
            <div class="small-text gw-templates__basis-button">
              {{ $t('message.DELETE') }}
            </div>
          </div>
          <GwTemplate
            v-for="template in team.gwTemplates"
            :key="template.id"
            :gw="template"
            @delete="deleteTemplate(template)"
            @copy="copyTemplate(template)"
            @start="startTemplate(template)"
          />
          <div
            v-if="team.id === currentTeam.id"
            class="gw-templates__new generic-border generic-border--text-btn v-centerer"
            @click="createTemplate"
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
              <span>{{ $t("message.GW_NEW_TEMPLATE") }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * GwTemplates
 * Component used as a page to display the team's available templates
 * to carry out a field tour (or Gemba Walk)
 *
 */

import toastr from 'toastr';
import { isEqual } from 'lodash';
// Vuex
import { createNamespacedHelpers } from 'vuex';
import { GET_GW_TEMPLATES } from '@/store/modules/gemba-walks/mutation-types';
import {
  FETCH_GW_TEMPLATES,
  START_GW,
  DUPLICATE_GW,
  DELETE_GW_TEMPLATE,
  SET_GW,
} from '@/store/modules/gemba-walks/action-types';
// Components
import ComponentsList from '@/components/components-list.vue';
import GwTemplate from '@/components/gw-template.vue';
import Icon from '@/components/icon.vue';
import FiltersZone from '@/components/filters-zone.vue';
// Mixins
import mixinGwTemplatesOptions from '@/mixins/page-options/gw-templates.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils & assets
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { calert } from '@/utils/main';
// eslint-disable-next-line import/no-cycle
import { createGwCreationLink } from '@/utils/navigation';
import { addExtraBold, arrowDownLine } from '@/assets/icons';

const { mapState: gwMapState, mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

export default {

  name: 'GwTemplates',

  components: {
    ComponentsList,
    GwTemplate,
    Icon,
    FiltersZone,
  },

  mixins: [mixinSocketHandler, mixinGwTemplatesOptions],

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      demoTmp: [],
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.GW_TEMPLATES'),
    };
  },

  computed: {
    compTeams() {
      if (this.demoMode) {
        return [{ ...this.currentTeam, gwTemplates: this.demoTmp }];
      }
      const teamIds = this.gwtOptSelectedTeams.length > 0
        ? this.gwtOptSelectedTeams
        : Object.keys(this.$store.state.gembaWalks.templatesPerTeam).map(id => Number(id));
      if (this.gwtOptSelectedTeams.length === 0 && !teamIds.includes(this.currentTeam.id)) {
        teamIds.push(this.currentTeam.id);
      }
      return teamIds
        .map((teamId) => {
          const team = this.allTeamsPerId[`${teamId}`];
          return { ...team, gwTemplates: this.$store.state.gembaWalks.templatesPerTeam[`${teamId}`] || [] };
        }, [])
        .sort((team1, team2) => {
          if (team1.id === this.currentTeam.id) {
            return -1;
          }
          return team2.id === this.currentTeam.id ? 1 : 0;
        });
    },
    /** Array: Override of mixinGwTemplatesOptions to set the value for the teams picker */
    gwtOptSelectedTeams: {
      get() {
        if (this.$route.query.gwtOptSelectedTeams) {
          return this.$route.query.gwtOptSelectedTeams
            .split(',')
            .map(v => Number(v))
            .filter((teamId) => {
              const team = this.allTeamsPerId[`${teamId}`];
              return team && team.hierarchyLevel <= this.currentTeam.hierarchyLevel;
            });
        }
        return [];
      },
      set(teams) {
        teams = teams instanceof Array ? teams : [];
        const query = Object.assign({}, this.$route.query);
        query.gwtOptSelectedTeams = teams.map(v => Number(v)).join(',');
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Object: allTeamsTree as an object in the following format: { <team_id>: <team_object>, ... }  */
    allTeamsPerId() {
      return this.$store.getters.allTeamsPerId;
    },
    ...gwMapState({
      templatesLoading: state => state[GET_GW_TEMPLATES.loadingKey],
    }),
  },

  watch: {
    gwtOptSelectedTeams() {
      this.refresh();
    },
    currentTeam: {
      handler(newTeam, oldTeam) {
        // When switching between teams, forget query params
        if (newTeam.id !== oldTeam.id) {
          this.gwtOptSelectedTeams = [];
        }
      },
      deep: true,
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    /** Fetch templates */
    refresh() {
      if (!this.templatesLoading) {
        this.FETCH_GW_TEMPLATES({ teams: this.gwtOptSelectedTeams.length ? this.gwtOptSelectedTeams : undefined });
      }
    },
    /** A value of the page options has been updated */
    paramsEdit(option) {
      if (this[option.key] !== undefined) {
        this[option.key] = option.value;
      }
    },
    /** Push the page to create a new template */
    createTemplate() {
      toastr.info(this.$t('message.GW_CREATING_TEMPLATE'));
      this.SET_GW({ id: 0 }).then((response) => {
        if (response && response.data instanceof Object) {
          this.$router.push(createGwCreationLink(response.data.id, this.$route));
        }
      });
    },
    /** Start a new field tour from the specified template */
    startTemplate(template) {
      this.START_GW({ template: template.id }).then((response) => {
        if (response && response.data instanceof Object && response.data.id) {
          toastr.info(this.$t('message.STARTING'));
          this.$router.push(this.getGwOngoingLink(response.data.id));
        }
      });
    },
    /** Template deletion */
    deleteTemplate(gw) {
      this.DELETE_GW_TEMPLATE({ id: gw.id });
    },
    /** Template duplication */
    copyTemplate(template) {
      const newName = this.$t('message.COPY_OF', { sth: template.name });
      calert(
        this.$t('message.GW_DUPLICATION_NAME'),
        [
          {
            title: this.$t('message.OK'),
            action: (fields) => {
              if (fields && fields[0] !== undefined) {
                this.DUPLICATE_GW({ id: template.id, name: fields[0] });
              }
            },
          },
          {
            title: this.$t('message.CANCEL'),
          },
        ],
        true,
        '',
        [{ ph: newName, value: newName }],
      );
    },
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.GW_TMP_INTRO')),
        new IAHelpStep('.gw-template .name', this.$t('help.GW_TMP_ITEM')),
        new IAHelpStep('.gw-template .start', this.$t('help.GW_TMP_START')),
        new IAHelpStep('.gw-template .edit-start', this.$t('help.GW_TMP_EDIT_START')),
        new IAHelpStep('.gw-template .copy', this.$t('help.GW_TMP_DUPLICATE')),
        new IAHelpStep('.gw-template .delete', this.$t('help.GW_TMP_DELETE')),
      ];
    },
    makeDemoValues() {
      this.demoTmp = [
        {
          date: null, duration: '1000', id: '1', name: this.$t('help.DEMO_EVENT3'), template: '0',
        },
      ];
    },
    ...gwMapActions({
      SET_GW,
      FETCH_GW_TEMPLATES,
      START_GW,
      DUPLICATE_GW,
      DELETE_GW_TEMPLATE,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-templates {
  overflow: visible;

  &__team {
    &:not(:first-child) {
      margin-top: 2rem;
    }
  }

  &__new {
    margin-top: 0.8rem;
    max-width: 30rem;

    &.generic-border--text-btn {
      background: white;
    }
  }

  &__col-headers {
    display: flex;
    align-items: center;

    > *:not(:first-child) {
      text-align: center;
    }

    &__column {
      text-align: center;
    }
  }

  &__content {
    > *:last-child {
      margin-bottom: 7em;
    }
  }

  // Align column headers with the content of gw-template
  // WARNING: These values are perfectly synced with gw-tempalte__basis-...
  // Changing any value here should imply a change in GWTemplate as well.
  &__basis-name {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__basis-duration {
    flex: 0 0 5rem;
    margin-right: 15%;
  }
  &__basis-button {
    flex: 0 0 6.5rem;
    padding: 0 .3em;
    box-sizing: border-box;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}

@media (max-width: $tablet-landscape) {
  .gw-templates {
    &__basis-duration {
      margin-right: 7.5%;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .gw-templates {
    &__basis-duration {
      margin-right: 3%;
    }

    &__basis-button {
      flex: 0 0 4.5rem;

      &:last-child {
        margin-right: .85rem;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .gw-templates {
    &__basis-duration {
      margin-right: 1%;
    }

    &__basis-button {
      flex: 0 0 4rem;
    }
  }
}

@media (max-width: $small-smartphone-portrait) {
  .gw-templates {
    &__basis-duration {
      display: none;
    }
  }
}

</style>
